import { useEffect, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { VideoPlayer } from "../components/VideoPlayer";
import { AdminGenerationPanel } from "../components/ai/AdminGenerationPanel";
import { TagPicker } from "../components/TagPicker";
import { AddToPlaylistModal } from "../components/AddToPlaylistModal";
import { setLightboxPref } from "../util/lightboxPref";
import { buildBackHref } from "../util/detail-url";
import { filterToParams } from "../search/filter-to-params";

interface AiMeta {
  generator?: string | null;
  model?: string | null;
  modelHash?: string | null;
  prompt?: string | null;
  negativePrompt?: string | null;
  sampler?: string | null;
  scheduler?: string | null;
  seed?: string | null;
  steps?: number | null;
  cfg?: number | null;
  size?: { w: number; h: number } | null;
  loras?: string[];
  version?: string | null;
}

interface MediaDetailProps {
  id: number;
  set: string;
  /** auto=slideshow plays photo slideshow on mount; auto=playall plays through videos. */
  auto: string | null;
  /** When true, start in lightbox (no chrome / metadata) — used when arriving from a tile tap. */
  lightbox: boolean;
  /** Carry the grid's duration filter into next/prev navigation so the user stays in the same slice. */
  minSeconds: number | null;
  maxSeconds: number | null;
  /** Carry the grid's search text + sort so "back" returns to the refined view, not the empty list. */
  q: string | null;
  order: string | null;
  /** Carry the grid's include-tag filter through detail navigation. */
  includeTagSlugs: string[];
  /** Carry the grid's exclude-tag filter through detail navigation. */
  excludeTagSlugs: string[];
  /** Encoded structured search filter — carried so ← back rebuilds the same refined grid. */
  filter: string | null;
  /** Random-shuffle seed when grid sort was "random". */
  seed: string | null;
  /** When true, start in endless-random play mode (from ?shuffle=1). */
  shuffle: boolean;
  /** Default next-button scope. URL > localStorage > "display". */
  nextMode: "display" | "folder" | null;
  isAdmin: boolean;
}

const SLIDESHOW_INTERVALS = [1, 3, 5, 10, 30] as const;

const LOOP_KEY = "mv-loop";

function initialLoop(): boolean {
  try {
    return window.localStorage.getItem(LOOP_KEY) === "1";
  } catch {
    return false;
  }
}

const NEXT_MODE_KEY = "mv-next-mode";
function initialNextMode(propValue: "display" | "folder" | null): "display" | "folder" {
  if (propValue === "display" || propValue === "folder") return propValue;
  try {
    const stored = window.localStorage.getItem(NEXT_MODE_KEY);
    if (stored === "folder" || stored === "display") return stored;
  } catch {
    /* ignore */
  }
  return "display";
}

export function MediaDetail({
  id,
  set,
  auto,
  lightbox,
  minSeconds,
  maxSeconds,
  q,
  order,
  includeTagSlugs,
  excludeTagSlugs,
  filter,
  seed,
  nextMode,
  shuffle,
  isAdmin,
}: MediaDetailProps) {
  const qc = useQueryClient();
  const [plOpen, setPlOpen] = useState(false);
  const detailQuery = useQuery({
    queryKey: ["media", id],
    queryFn: () => api.mediaDetail(id),
    // Keep showing the previous asset while the next one's data is
    // fetching, so slideshow / play-all advances don't blink to a
    // "Loading…" placeholder mid-sequence. Without this, the top-level
    // `if (detailQuery.isPending) return <Loading>` short-circuits the
    // immersive view on every nav.
    placeholderData: keepPreviousData,
  });
  const [nextModeOn, setNextModeOnState] = useState<"display" | "folder">(() => initialNextMode(nextMode));
  const setNextModeOn = (mode: "display" | "folder"): void => {
    setNextModeOnState(mode);
    try {
      window.localStorage.setItem(NEXT_MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  };
  const neighborsQuery = useQuery({
    queryKey: [
      "neighbors",
      id,
      set,
      minSeconds,
      maxSeconds,
      includeTagSlugs,
      excludeTagSlugs,
      nextModeOn,
      order,
      seed,
      filter,
    ],
    queryFn: () =>
      api.neighbors(id, set, {
        minSeconds: minSeconds ?? undefined,
        maxSeconds: maxSeconds ?? undefined,
        tags: includeTagSlugs.length > 0 ? includeTagSlugs : undefined,
        excludeTags: excludeTagSlugs.length > 0 ? excludeTagSlugs : undefined,
        nextMode: nextModeOn,
        order: nextModeOn === "display" ? (order as "newest" | "oldest" | "added_new" | "added_old" | "random" | undefined) ?? undefined : undefined,
        seed: nextModeOn === "display" && seed ? seed : undefined,
        filter: nextModeOn === "display" && filter ? filter : undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const stageRef = useRef<HTMLDivElement | null>(null);
  const videoEndRef = useRef<(() => void) | null>(null);
  // Touch tracking for swipe-to-navigate. We track the *first* finger only;
  // pinch/multi-touch is left to the browser (zoom on photos, scrub on video).
  const touchStartRef = useRef<{ x: number; y: number; t: number } | null>(null);
  // Unified auto-advance mode. Photos use the slideshow timer; videos
  // autoplay and advance on `ended`. The two URL tokens (`slideshow` and
  // `playall`) are kept as aliases for backward-compatible deep links
  // and to choose a sensible default initial behavior. Internally we
  // collapse them into a single flag so a mixed playlist or tag set
  // doesn't stall when it transitions between media kinds.
  const autoInitial = auto === "slideshow" || auto === "playall";
  const [autoOn, setAutoOn] = useState(autoInitial);
  const [shuffleOn, setShuffleOn] = useState(shuffle);
  const [loopOn, setLoopOn] = useState<boolean>(initialLoop);
  // When a photo's 1024px thumbnail isn't generated yet (e.g. a fresh bulk
  // ingest still draining the thumb queue), the thumb endpoint 404s. Fall
  // back to the full original so the image still shows. Reset per asset so
  // each photo first tries its cheaper thumbnail.
  const [photoSrcFellBack, setPhotoSrcFellBack] = useState(false);
  useEffect(() => {
    setPhotoSrcFellBack(false);
  }, [detailQuery.data?.id]);
  useEffect(() => {
    try {
      window.localStorage.setItem(LOOP_KEY, loopOn ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [loopOn]);
  const [slideshowMs, setSlideshowMs] = useState(5000);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Lightbox = CSS-only full-viewport mode (works on iOS where requestFullscreen
  // refuses non-video elements). Distinct from browser fullscreen but renders
  // the same way; either flag triggers immersive UI.
  // Wrapped so every transition writes the user's preference to localStorage —
  // tile clicks on subsequent assets read this to honor the user's last choice.
  const [lightboxOn, setLightboxOnState] = useState(lightbox);
  const setLightboxOn = (on: boolean): void => {
    setLightboxOnState(on);
    setLightboxPref(on);
  };
  const immersive = lightboxOn || isFullscreen;

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Sync the pref on mount so a deep-link with ?lightbox=1 records the user's
  // implicit preference, and a deep-link without it forces the pref off.
  useEffect(() => {
    setLightboxPref(lightbox);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function navTo(nextId: number, opts: { auto?: "slideshow" | "playall" } = {}): void {
    const params = new URLSearchParams();
    params.set("set", set);
    if (opts.auto) params.set("auto", opts.auto);
    if (shuffleOn) params.set("shuffle", "1");
    if (lightboxOn) params.set("lightbox", "1"); // preserve lightbox across navigation
    // Carry the duration filter so neighbor lookups on the next page
    // see the same slice — without this, hitting Next from a filtered
    // detail view would silently widen the set on the first navigation.
    if (minSeconds != null) params.set("minSeconds", String(minSeconds));
    if (maxSeconds != null) params.set("maxSeconds", String(maxSeconds));
    // Same idea for search and sort — preserved so "back" goes to the
    // refined listing instead of the empty default view.
    if (q) params.set("q", q);
    if (order) params.set("order", order);
    // Carry the tag filter so neighbors on the next page stay inside
    // the same chip-filtered slice (same reason as duration/search above).
    if (includeTagSlugs.length > 0) params.set("tags", includeTagSlugs.join(","));
    if (excludeTagSlugs.length > 0) params.set("excludeTags", excludeTagSlugs.join(","));
    if (filter) params.set("filter", filter);
    if (seed) params.set("seed", seed);
    params.set("next", nextModeOn);
    // Client-side route change. `window.location.href` would do a full
    // page reload — the SPA tears down and rebuilds for every asset,
    // and the user sees the immersive overlay flash off and back on
    // between each item in a slideshow/play-all. pushState updates the
    // URL while keeping the app mounted; MediaDetail's local state
    // (lightboxOn, autoOn, chromeVisible) survives the transition so
    // fullscreen and auto-advance carry across the whole sequence.
    // App.tsx's URL hooks listen for popstate, so we dispatch one to
    // trigger their state update.
    const url = `/media/${nextId}?${params}`;
    window.history.pushState({}, "", url);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  // Forward motion for auto-advance and the Next button. In shuffle mode we
  // ask the server for a random item in the same set (excluding the current
  // one); otherwise we use the precomputed neighbor / loop-to-first.
  //
  // The slideshow timer captures this closure, so it reads includeTagSlugs /
  // excludeTagSlugs without them being in the effect deps. That's safe only
  // because tag slugs are URL-derived and can change solely via a full
  // navigation, which also changes `id` (a dep) and re-keys neighborsQuery.
  // If an in-viewer tag editor is ever added, add the slugs to the slideshow
  // effect deps.
  async function advanceNext(): Promise<void> {
    const autoOpt = autoOn ? { auto: "playall" as const } : {};
    if (shuffleOn) {
      try {
        const r = await api.random({
          set,
          tags: includeTagSlugs.length > 0 ? includeTagSlugs : undefined,
          excludeTags: excludeTagSlugs.length > 0 ? excludeTagSlugs : undefined,
          exclude: id,
        });
        navTo(r.id, autoOpt);
      } catch {
        // 404 (no other item in the set) or transient error — stop playing.
        if (autoOn) setAutoOn(false);
      }
      return;
    }
    const n = neighborsQuery.data;
    if (n?.next != null) navTo(n.next, autoOpt);
    else if (loopOn && n?.first != null && n.first !== id) navTo(n.first, autoOpt);
    else if (autoOn) setAutoOn(false);
  }

  function go(direction: "prev" | "next"): void {
    if (direction === "next") {
      void advanceNext();
      return;
    }
    // Prev: in shuffle mode the visited sequence lives in the browser history
    // (each advance is a pushState entry), so step back through it.
    if (shuffleOn) {
      window.history.back();
      return;
    }
    const n = neighborsQuery.data;
    if (n?.prev != null) navTo(n.prev, autoOn ? { auto: "playall" } : {});
  }

  function toggleShuffle(): void {
    const next = !shuffleOn;
    setShuffleOn(next);
    if (next) setAutoOn(true);
    // Reflect the mode in the URL (without navigating) so a refresh keeps it.
    const params = new URLSearchParams(window.location.search);
    if (next) {
      params.set("shuffle", "1");
      params.set("auto", "playall");
    } else {
      params.delete("shuffle");
    }
    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "ArrowRight") go("next");
      else if (e.key === "ArrowLeft") go("prev");
      else if (e.key.toLowerCase() === "f") toggleFullscreen();
      else if (e.key === "Escape") {
        if (autoOn) setAutoOn(false);
        else if (lightboxOn) setLightboxOn(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborsQuery.data, set, autoOn, lightboxOn, shuffleOn, id]);

  // Slideshow timer (photos). With auto-advance on, each photo is shown
  // for slideshowMs then we jump to the next item. Videos use their own
  // end-handler — see videoEndRef below.
  useEffect(() => {
    if (!autoOn) return;
    const m = detailQuery.data;
    if (!m || m.kind !== "photo") return;
    const t = setTimeout(() => {
      void advanceNext();
    }, slideshowMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOn, slideshowMs, detailQuery.data, neighborsQuery.data, loopOn, id, shuffleOn]);

  // Video end handler — advance once the clip finishes. Without the
  // autoOn gate, every video would advance even when the user opened it
  // standalone.
  videoEndRef.current = () => {
    if (!autoOn) return;
    void advanceNext();
  };

  // Swipe-to-navigate, photo-only. Videos are excluded from the swipe
  // path so we don't fight native scrubbing / play-pause / double-tap-
  // seek gestures (the VideoPlayer handles its own touch logic). Tap
  // and long-press are tracked for BOTH kinds so chrome can be revealed
  // in fullscreen on either.
  const wasSwipeRef = useRef(false);

  // Immersive-mode chrome auto-hide. Default visible on entry to
  // immersive mode; fades out after CHROME_HIDE_MS of inactivity.
  // Re-shown on: pointer move (desktop), tap (mobile), long-press
  // (mobile). The hide timer is reset on every show.
  const CHROME_HIDE_MS = 3000;
  const LONG_PRESS_MS = 500;
  const TAP_SLOP_PX = 10;
  const [chromeVisible, setChromeVisible] = useState(true);
  const chromeHideTimerRef = useRef<number | null>(null);
  const longPressTimerRef = useRef<number | null>(null);
  function showChromeMomentarily(): void {
    setChromeVisible(true);
    if (chromeHideTimerRef.current != null) {
      window.clearTimeout(chromeHideTimerRef.current);
    }
    chromeHideTimerRef.current = window.setTimeout(() => {
      setChromeVisible(false);
      chromeHideTimerRef.current = null;
    }, CHROME_HIDE_MS);
  }

  // Entering immersive → arm the auto-hide. Leaving → cancel and pin
  // chrome visible (non-immersive view always shows the toolbar).
  useEffect(() => {
    if (immersive) {
      showChromeMomentarily();
    } else {
      setChromeVisible(true);
      if (chromeHideTimerRef.current != null) {
        window.clearTimeout(chromeHideTimerRef.current);
        chromeHideTimerRef.current = null;
      }
    }
    return () => {
      if (chromeHideTimerRef.current != null) {
        window.clearTimeout(chromeHideTimerRef.current);
        chromeHideTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immersive]);

  // Desktop: any pointer movement in immersive mode reveals chrome and
  // restarts the hide timer. Mobile relies on the touch handlers below.
  useEffect(() => {
    if (!immersive) return;
    const onMove = (): void => showChromeMomentarily();
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immersive]);

  function onTouchStart(e: React.TouchEvent): void {
    wasSwipeRef.current = false;
    if (e.touches.length !== 1) return;
    const t = e.touches[0]!;
    touchStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    // Long-press anywhere in immersive mode reveals chrome. Cancelled
    // by significant finger movement (see onTouchMove) or by an early
    // touchend (see onTouchEnd) — neither of which counts as a hold.
    if (immersive && longPressTimerRef.current == null) {
      longPressTimerRef.current = window.setTimeout(() => {
        showChromeMomentarily();
        longPressTimerRef.current = null;
        // Suppress the synthesized click that may follow a long-press
        // release — otherwise a long-press on a photo would both
        // reveal chrome AND advance.
        wasSwipeRef.current = true;
      }, LONG_PRESS_MS);
    }
  }

  function onTouchMove(e: React.TouchEvent): void {
    if (longPressTimerRef.current != null && touchStartRef.current) {
      const t = e.touches[0]!;
      const dx = t.clientX - touchStartRef.current.x;
      const dy = t.clientY - touchStartRef.current.y;
      if (Math.abs(dx) > TAP_SLOP_PX || Math.abs(dy) > TAP_SLOP_PX) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  }

  function onTouchEnd(e: React.TouchEvent): void {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.t;

    // Photo swipe: 50px horizontal, mostly horizontal, under 600ms.
    if (
      detailQuery.data?.kind === "photo" &&
      Math.abs(dx) >= 50 &&
      Math.abs(dx) > Math.abs(dy) &&
      dt <= 600
    ) {
      wasSwipeRef.current = true;
      if (dx < 0) go("next");
      else go("prev");
      return;
    }

    // Small-movement, short-duration touch in immersive mode = tap.
    // Reveal chrome and suppress the photo's click-to-advance handler
    // so a tap doesn't both show chrome AND skip to the next item.
    if (
      immersive &&
      Math.abs(dx) < TAP_SLOP_PX &&
      Math.abs(dy) < TAP_SLOP_PX &&
      dt < 500
    ) {
      showChromeMomentarily();
      wasSwipeRef.current = true;
    }
  }

  function toggleFullscreen(): void {
    if (immersive) {
      // Exit lightbox. Also exit browser fullscreen if the user entered it
      // some other way (F11) — otherwise leaving lightbox while still in
      // browser FS would look stuck.
      if (document.fullscreenElement) void document.exitFullscreen();
      setLightboxOn(false);
    } else {
      // CSS-only lightbox. We deliberately don't call requestFullscreen —
      // browser fullscreen behaves badly on iOS (rejected on non-video
      // elements), interferes with swipe gestures, and the lightbox already
      // covers the whole viewport visually.
      setLightboxOn(true);
    }
  }

  const toggleFav = useMutation({
    mutationFn: ({ on }: { on: boolean }) => api.toggleFavorite(id, on),
    onMutate: async ({ on }) => {
      await qc.cancelQueries({ queryKey: ["media", id] });
      const prev = qc.getQueryData(["media", id]);
      qc.setQueryData(["media", id], (cur: any) => (cur ? { ...cur, favorited: on } : cur));
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["media", id], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["media", id] });
      qc.invalidateQueries({ queryKey: ["favorites"] });
    },
  });

  const del = useMutation({
    mutationFn: () => api.deleteMedia(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["favorites"] });
      qc.invalidateQueries({ queryKey: ["playlists"] });
      // Navigate back to where the user came from in the set context
      window.location.href = buildBackHref({ set, filter, order, includeTagSlugs, excludeTagSlugs });
    },
  });

  if (detailQuery.isPending) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (detailQuery.isError) return <div className="p-8 text-red-300">Error: {String(detailQuery.error)}</div>;
  const m = detailQuery.data;
  const setLabel = describeSet(set);
  const prevId = neighborsQuery.data?.prev ?? null;
  const nextId = neighborsQuery.data?.next ?? null;

  return (
    <div className={immersive ? "" : "p-6"}>
      {!immersive && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm">
            <a href={buildBackHref({ set, filter, order, includeTagSlugs, excludeTagSlugs })} className="text-zinc-400 hover:text-zinc-200">
              ← back
            </a>
            <span className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">
              set: {setLabel}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => go("prev")}
              disabled={!shuffleOn && prevId == null}
              className="rounded bg-zinc-800 px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-30"
              title="Previous (←)"
            >
              ← Prev
            </button>
            <button
              onClick={() => go("next")}
              disabled={!shuffleOn && nextId == null}
              className="rounded bg-zinc-800 px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-30"
              title="Next (→)"
            >
              Next →
            </button>
            <div
              className="ml-1 flex overflow-hidden rounded border border-zinc-700 text-xs"
              title="What 'Next' means: Display = the grid's current sort + filter. Folder = same folder on disk, filename ascending."
            >
              <button
                onClick={() => setNextModeOn("display")}
                className={
                  "px-2 py-1 " +
                  (nextModeOn === "display"
                    ? "bg-zinc-700 text-white"
                    : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800")
                }
              >
                Display
              </button>
              <button
                onClick={() => setNextModeOn("folder")}
                className={
                  "px-2 py-1 " +
                  (nextModeOn === "folder"
                    ? "bg-zinc-700 text-white"
                    : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800")
                }
              >
                Folder
              </button>
            </div>
            <button
              onClick={() => setAutoOn((on) => !on)}
              className={
                "rounded px-3 py-1.5 text-sm " +
                (autoOn
                  ? "bg-emerald-700 text-white hover:bg-emerald-600"
                  : "bg-zinc-800 hover:bg-zinc-700")
              }
              title="Auto-advance through this set — photos slide, videos autoplay"
            >
              {autoOn ? "▮▮ Pause" : "▶ Play all"}
            </button>
            <button
              onClick={toggleShuffle}
              className={
                "rounded px-3 py-1 text-xs " +
                (shuffleOn
                  ? "bg-violet-700 text-white hover:bg-violet-600"
                  : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700")
              }
              title="Shuffle — play random items from this set"
            >
              🎲 Shuffle
            </button>
            <button
              onClick={() => setLoopOn((on) => !on)}
              className={
                "rounded px-3 py-1 text-xs " +
                (loopOn
                  ? "bg-sky-700 text-white hover:bg-sky-600"
                  : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700")
              }
              title="Loop the set (or this clip)"
            >
              🔁 Loop
            </button>
            <button
              onClick={toggleFullscreen}
              className="rounded bg-zinc-800 px-3 py-1.5 text-sm hover:bg-zinc-700"
              title="Fullscreen (F)"
            >
              ⛶ Fullscreen
            </button>
            <button
              onClick={() => toggleFav.mutate({ on: !m.favorited })}
              className={
                "rounded px-3 py-1.5 text-sm transition-colors " +
                (m.favorited
                  ? "bg-rose-700/40 text-rose-200 hover:bg-rose-700/60"
                  : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700")
              }
            >
              {m.favorited ? "♥ Favorited" : "♡ Favorite"}
            </button>
            <button
              onClick={() => setPlOpen(true)}
              className="rounded bg-zinc-800 px-3 py-1.5 text-sm hover:bg-zinc-700"
              title="Add to playlist"
            >
              📂 Playlist
            </button>
            {isAdmin && (
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete this ${m.kind}?\n\n${m.relativePath}\n\nThe file will be moved to /media/.media-viewer/trash/.`,
                    )
                  ) {
                    del.mutate();
                  }
                }}
                className="rounded bg-rose-800/60 px-3 py-1.5 text-sm text-rose-100 hover:bg-rose-800"
                title="Delete (admin)"
              >
                🗑 Delete
              </button>
            )}
          </div>
        </div>
      )}

      {!immersive && autoOn && m.kind === "photo" && (
        <div className="mb-3 flex items-center gap-2 rounded bg-zinc-900 px-3 py-2 text-xs text-zinc-300">
          <span className="text-zinc-500">Slide every:</span>
          {SLIDESHOW_INTERVALS.map((sec) => (
            <button
              key={sec}
              onClick={() => setSlideshowMs(sec * 1000)}
              className={
                "rounded px-2 py-0.5 " +
                (slideshowMs === sec * 1000
                  ? "bg-emerald-700 text-white"
                  : "bg-zinc-800 hover:bg-zinc-700")
              }
            >
              {sec}s
            </button>
          ))}
          <span className="ml-auto text-zinc-500">Esc to stop</span>
        </div>
      )}

      <div
        ref={stageRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={
          "relative flex items-center justify-center bg-black " +
          (immersive
            ? "fixed inset-0 z-40 h-screen w-screen touch-pan-y select-none"
            : "min-h-[60vh] rounded p-2")
        }
      >
        {m.kind === "photo" ? (
          <img
            src={m.isAnimated || photoSrcFellBack ? m.originalUrl : `${m.thumbUrl}?size=1024`}
            onError={() => {
              if (!m.isAnimated && !photoSrcFellBack) setPhotoSrcFellBack(true);
            }}
            alt=""
            draggable={false}
            className={
              immersive
                ? "max-h-screen max-w-full object-contain"
                : "max-h-[80vh] max-w-full object-contain"
            }
            // In immersive mode, click the photo (mouse) to advance. On touch,
            // swipes handle prev/next; we suppress the synthesized click that
            // follows a swipe-touchend so a single swipe doesn't double-advance.
            onClick={() => {
              if (wasSwipeRef.current) {
                wasSwipeRef.current = false;
                return;
              }
              if (immersive && m.kind === "photo") go("next");
            }}
          />
        ) : (
          <VideoPlayer
            streamUrl={m.streamUrl}
            posterUrl={m.posterUrl}
            autoPlay={autoInitial}
            onEnded={() => videoEndRef.current?.()}
            loop={loopOn && !autoOn}
          />
        )}

        {immersive && (
          <div
            // Fade container for all immersive chrome. Auto-hides 3s
            // after the last pointer move / tap / long-press so the
            // image or video occupies the screen without UI interfering.
            //
            // Pointer-events ladder: the wrapper itself is always
            // pointer-events-none so clicks on its empty space pass
            // through to the underlying photo (which handles
            // click-to-advance on desktop). The arbitrary descendant
            // selector re-enables pointer-events on every button/anchor
            // ONLY when chrome is visible — so hidden buttons can't
            // catch invisible clicks.
            className={
              "absolute inset-0 z-50 pointer-events-none transition-opacity duration-200 " +
              (chromeVisible
                ? "opacity-100 [&_button]:pointer-events-auto [&_a]:pointer-events-auto"
                : "opacity-0")
            }
          >
            {(shuffleOn || prevId != null) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  go("prev");
                }}
                className="absolute left-2 top-1/2 z-50 -translate-y-1/2 rounded-full bg-black/60 px-4 py-3 text-2xl text-white hover:bg-black/80 sm:left-4"
                aria-label="Previous"
              >
                ←
              </button>
            )}
            {(shuffleOn || nextId != null) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  go("next");
                }}
                className="absolute right-2 top-1/2 z-50 -translate-y-1/2 rounded-full bg-black/60 px-4 py-3 text-2xl text-white hover:bg-black/80 sm:right-4"
                aria-label="Next"
              >
                →
              </button>
            )}

            {/* Top-left: close — exits to grid */}
            <a
              href={buildBackHref({ set, filter, order, includeTagSlugs, excludeTagSlugs })}
              className="absolute left-2 top-2 z-50 rounded bg-black/60 px-3 py-1.5 text-sm text-white hover:bg-black/80 sm:left-4 sm:top-4"
              title="Close"
            >
              ✕
            </a>

            {/* Top-right: action overlay */}
            <div className="absolute right-2 top-2 z-50 flex flex-wrap items-center gap-1.5 sm:right-4 sm:top-4">
              <button
                onClick={() => toggleFav.mutate({ on: !m.favorited })}
                className={
                  "rounded px-2.5 py-1 text-sm " +
                  (m.favorited
                    ? "bg-rose-700 text-white hover:bg-rose-600"
                    : "bg-black/60 text-white hover:bg-black/80")
                }
                title={m.favorited ? "Unfavorite" : "Favorite"}
              >
                {m.favorited ? "♥" : "♡"}
              </button>
              <button
                onClick={() => setPlOpen(true)}
                className="rounded bg-black/60 px-2.5 py-1 text-sm text-white hover:bg-black/80"
                title="Add to playlist"
              >
                📂
              </button>
              <button
                onClick={() => setAutoOn((on) => !on)}
                className={
                  "rounded px-2.5 py-1 text-sm " +
                  (autoOn ? "bg-emerald-700 text-white" : "bg-black/60 text-white hover:bg-black/80")
                }
                title="Play all (auto-advance through this set)"
              >
                {autoOn ? "▮▮" : "▶"}
              </button>
              <button
                onClick={toggleShuffle}
                className={
                  "rounded px-2.5 py-1 text-sm " +
                  (shuffleOn ? "bg-violet-700 text-white" : "bg-black/60 text-white hover:bg-black/80")
                }
                title="Shuffle (random from this set)"
              >
                🎲
              </button>
              <button
                onClick={() => setLoopOn((on) => !on)}
                className={
                  "rounded px-2.5 py-1 text-sm " +
                  (loopOn
                    ? "bg-sky-700 text-white"
                    : "bg-black/60 text-white hover:bg-black/80")
                }
                title="Loop"
              >
                🔁
              </button>
              {isAdmin && (
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete this ${m.kind}?\n\n${m.relativePath}\n\nThe file will be moved to /media/.media-viewer/trash/.`,
                      )
                    ) {
                      del.mutate();
                    }
                  }}
                  className="rounded bg-rose-800/80 px-2.5 py-1 text-sm text-white hover:bg-rose-800"
                  title="Delete (admin)"
                >
                  🗑
                </button>
              )}
              <button
                onClick={() => {
                  // Exit lightbox AND browser fullscreen if active.
                  if (document.fullscreenElement) void document.exitFullscreen();
                  setLightboxOn(false);
                }}
                className="rounded bg-black/60 px-2.5 py-1 text-sm text-white hover:bg-black/80"
                title="Show details"
              >
                ⓘ
              </button>
              <button
                onClick={() => setNextModeOn(nextModeOn === "display" ? "folder" : "display")}
                className="rounded bg-black/60 px-2.5 py-1 text-xs text-white hover:bg-black/80"
                title={`Next mode: ${nextModeOn} — click to swap`}
              >
                {nextModeOn === "display" ? "🖼" : "📁"}
              </button>
              <button
                onClick={toggleFullscreen}
                className="rounded bg-black/60 px-2.5 py-1 text-sm text-white hover:bg-black/80"
                title="Browser fullscreen (F)"
              >
                ⛶
              </button>
            </div>
          </div>
        )}
      </div>

      {!immersive && (
        // Single-column on mobile: Tags first (so it's the most visible
        // box under the asset on phones, where typing tags is the most
        // common action), then Details, then Actions.
        // 2-col on md+: Details left, Tags top-right, Actions below Tags.
        // Explicit grid placement so the source order matches mobile flow.
        <div className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
          <div className="rounded border border-zinc-800 bg-zinc-900 p-4 md:col-start-2 md:row-start-1">
            <h3 className="mb-2 text-base font-medium">Tags</h3>
            <TagPicker mediaId={id} current={m.tags} />
          </div>
          <div className="rounded border border-zinc-800 bg-zinc-900 p-4 md:col-start-1 md:row-span-2 md:row-start-1">
            <h3 className="mb-2 text-base font-medium">Details</h3>
            <dl className="space-y-1 text-zinc-300">
              <Row label="Path" value={<span className="break-all">{m.relativePath}</span>} />
              {m.originalIngestPath && (
                <Row
                  label="Origin"
                  value={
                    <span className="break-all text-zinc-400" title="Path under /ingest at import time">
                      {m.originalIngestPath}
                    </span>
                  }
                />
              )}
              <Row label="Size" value={formatBytes(m.sizeBytes)} />
              {m.width != null && m.height != null && (
                <Row label="Dimensions" value={`${m.width} × ${m.height}`} />
              )}
              {m.durationMs != null && (
                <Row label="Duration" value={`${(m.durationMs / 1000).toFixed(1)}s`} />
              )}
              {m.takenAt && <Row label="Taken" value={new Date(m.takenAt).toLocaleString()} />}
              <Row label="Ingested" value={new Date(m.ingestedAt).toLocaleString()} />
              {m.uploadedAt && <Row label="Uploaded" value={new Date(m.uploadedAt).toLocaleString()} />}
              <Row label="SHA-256" value={<code className="break-all text-xs text-zinc-400">{m.sha256}</code>} />
            </dl>
          </div>
          <div className="rounded border border-zinc-800 bg-zinc-900 p-4 md:col-start-2 md:row-start-2">
            <h3 className="mb-2 text-base font-medium">Actions</h3>
            <div className="flex flex-wrap gap-2">
              <a href={m.downloadUrl} className="rounded bg-zinc-800 px-3 py-1.5 text-sm hover:bg-zinc-700" download>
                Download original
              </a>
            </div>
            <p className="mt-3 text-xs text-zinc-500">
              Keyboard: ← / → navigate · F fullscreen · Esc stop slideshow
            </p>
          </div>
          {m.aiGeneration && (
            <div className="md:col-span-2">
              <AdminGenerationPanel info={m.aiGeneration} />
            </div>
          )}
          {(m.exif as { ai?: AiMeta } | null)?.ai && (
            <div className="rounded border border-zinc-800 bg-zinc-900 p-4 md:col-span-2">
              <GenerationSection ai={(m.exif as { ai: AiMeta }).ai} />
            </div>
          )}
        </div>
      )}
      <AddToPlaylistModal open={plOpen} onClose={() => setPlOpen(false)} mediaIds={[id]} />
    </div>
  );
}

function describeSet(set: string): string {
  if (set.startsWith("tag:")) return `tag "${set.slice(4)}"`;
  if (set.startsWith("folder:")) return `folder ${set.slice(7)}`;
  return set;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  // On phones, stack label-then-value vertically so long paths/hashes have
  // the full row width to wrap into. On sm+ go back to a 3-col grid.
  return (
    <div className="flex flex-col gap-0.5 sm:grid sm:grid-cols-3 sm:gap-2">
      <dt className="text-zinc-500">{label}</dt>
      <dd className="min-w-0 break-words sm:col-span-2">{value}</dd>
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function navigateToFilter(filter: import("@media-viewer/shared").StructuredFilter): void {
  const params = filterToParams(filter);
  const url = params ? `/?${params.toString()}` : "/";
  window.history.pushState({}, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function CollapsibleText({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {/* ignore */});
  }

  return (
    <div className="mt-1">
      <div className="flex items-center gap-2">
        <dt className="text-zinc-500">{label}</dt>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400 hover:bg-zinc-700"
        >
          {open ? "hide" : "show"}
        </button>
        <button
          onClick={copy}
          className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400 hover:bg-zinc-700"
        >
          {copied ? "copied!" : "copy"}
        </button>
      </div>
      {open && (
        <dd className="mt-1 whitespace-pre-wrap break-words rounded bg-zinc-800/60 p-2 text-xs text-zinc-300">
          {text}
        </dd>
      )}
    </div>
  );
}

function GenerationSection({ ai }: { ai: AiMeta }) {
  const modelLabel = ai.model
    ? ai.modelHash
      ? `${ai.model} (${ai.modelHash})`
      : ai.model
    : null;

  return (
    <>
      <h3 className="mb-2 text-base font-medium">Generation</h3>
      <dl className="space-y-1 text-zinc-300">
        {modelLabel && (
          <Row
            label="Model"
            value={
              <button
                onClick={() => navigateToFilter({ rawQuery: "", ai: { model: ai.model! } })}
                className="break-all text-left text-blue-400 underline-offset-2 hover:underline"
                title="Search for this model"
              >
                {modelLabel}
              </button>
            }
          />
        )}
        {ai.sampler && <Row label="Sampler" value={ai.sampler} />}
        {ai.scheduler && <Row label="Scheduler" value={ai.scheduler} />}
        {ai.steps != null && <Row label="Steps" value={String(ai.steps)} />}
        {ai.cfg != null && <Row label="CFG" value={String(ai.cfg)} />}
        {ai.seed && <Row label="Seed" value={<code className="text-xs text-zinc-400">{ai.seed}</code>} />}
        {ai.size && <Row label="Gen size" value={`${ai.size.w} × ${ai.size.h}`} />}
        {ai.version && <Row label="Version" value={<span className="text-zinc-400">{ai.version}</span>} />}
        {ai.loras && ai.loras.length > 0 && (
          <div className="flex flex-col gap-0.5 sm:grid sm:grid-cols-3 sm:gap-2">
            <dt className="text-zinc-500">LoRAs</dt>
            <dd className="min-w-0 sm:col-span-2">
              <div className="flex flex-wrap gap-1">
                {ai.loras.map((lora) => (
                  <button
                    key={lora}
                    onClick={() => navigateToFilter({ rawQuery: "", ai: { lora } })}
                    className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-blue-400 hover:bg-zinc-700"
                    title="Search for this LoRA"
                  >
                    {lora}
                  </button>
                ))}
              </div>
            </dd>
          </div>
        )}
      </dl>
      {ai.prompt && <CollapsibleText label="Prompt" text={ai.prompt} />}
      {ai.negativePrompt && <CollapsibleText label="Negative prompt" text={ai.negativePrompt} />}
    </>
  );
}
